import { useReducer } from "react";
import {
  AutocompleteModule,
  BuilderConfigModule,
  DropdownModule,
  HTMLModule,
  ImageBuilderConfigModule,
  ImageModule,
  ImageQuestionBuilderConfigModule,
  ImageQuestionModule,
  LabeledBuilderConfigModule,
  MultiSelectBuilderConfigModule,
  MultiSelectQuestionModule,
  RankedBuilderConfigModule,
  RankedQuestionModule,
  SelectBuilderConfigModule,
  SingleSelectBuilderConfigModule,
  SingleSelectQuestionModule,
  SongsQuestionBuilderConfigModule,
  SongsQuestionModule,
  SurveyQuestionModule,
  TextBuilderConfigModule,
  TextModule,
  TextQuestionBuilderConfigModule,
  TextQuestionModule,
} from "@max/common/dist/setfan";
import { QuestionType } from "Routes/SetFan/Components/Modal/AddQuestion/BaseQuestionModal";
import { cloneDeep } from "lodash";
import { useConfirm } from "Routes/SetFan/Components/ConfirmContext";
import { useBuilderContext } from "Routes/SetFan/Components/BuilderContext";
import { confirmDeleteQuestion } from "Routes/SetFan/constants/confirmationContent";

export type SurveyModulesState = {
  isOpen: boolean;
  route?: QuestionType;
  questions: SurveyQuestionModule[];
  existing?: SurveyQuestionModule;
};

export type SurveyModulesAction =
  | "closeModal"
  | "openModal"
  | "setRoute"
  | "addModule"
  | "moveModule"
  | "updateModule"
  | "removeModule"
  | "setModules";

export type SurveyModulesDispatch =
  | { type: "closeModal" }
  | {
      type: "openModal";
      payload?: { route?: QuestionType; existing?: SurveyQuestionModule };
    }
  | { type: "setRoute"; payload: QuestionType }
  | { type: "addModule"; payload: { module: SurveyQuestionModule } }
  | { type: "moveModule"; payload: { from: number; to: number } }
  | {
      type: "updateModule";
      payload: { idToReplace: string; module: SurveyQuestionModule };
    }
  | { type: "removeModule"; payload: number }
  | { type: "setModules"; payload: SurveyQuestionModule[] }
  | { type: "hideModule"; payload: number }
  | { type: "unhideModule"; payload: number };

function reducer(state: SurveyModulesState, action: SurveyModulesDispatch) {
  switch (action.type) {
    case "closeModal":
      return { ...state, isOpen: false, route: undefined, existing: undefined };
    case "openModal":
      return {
        ...state,
        isOpen: true,
        route: action.payload?.route,
        existing: action.payload?.existing,
      };
    case "setRoute":
      return { ...state, route: action.payload };
    case "addModule": {
      const clonedQuestions = cloneDeep(state.questions);
      clonedQuestions.push(action.payload.module);
      return { ...state, questions: clonedQuestions };
    }
    case "moveModule": {
      const clonedQuestions = cloneDeep(state.questions);
      const questionToMove = clonedQuestions.splice(action.payload.from, 1)[0];
      clonedQuestions.splice(action.payload.to, 0, questionToMove);
      return { ...state, questions: clonedQuestions };
    }
    case "updateModule": {
      const newModule = action.payload.module;
      const idToReplace = action.payload.idToReplace || newModule.id;
      const modIdx = state.questions.findIndex(
        (question) => question.id === idToReplace,
      );
      if (modIdx < 0) {
        throw new Error(
          `unable to find existing module to update: ${idToReplace}`,
        );
      }
      const clonedQuestions = cloneDeep(state.questions);
      clonedQuestions.splice(modIdx, 1, newModule);
      return { ...state, questions: clonedQuestions };
    }
    case "removeModule": {
      const clonedQuestions = cloneDeep(state.questions);
      clonedQuestions.splice(action.payload, 1);
      return { ...state, questions: clonedQuestions };
    }
    case "setModules": {
      const clonedQuestions = cloneDeep(action.payload);
      return { ...state, questions: clonedQuestions };
    }
    case "hideModule": {
      const clonedQuestions = cloneDeep(state.questions);
      clonedQuestions[action.payload].hidden = true;
      if (clonedQuestions[action.payload].required) {
        clonedQuestions[action.payload].required = false;
      }
      return { ...state, questions: clonedQuestions };
    }
    case "unhideModule": {
      const clonedQuestions = cloneDeep(state.questions);
      clonedQuestions[action.payload].hidden = false;
      return { ...state, questions: clonedQuestions };
    }
    default:
      throw new Error(
        `unexpected action in survey questions reducer: ${action}`,
      );
  }
}

export const useAddSurveyModules = (initialState?: BuilderConfigModule[]) => {
  const [state, dispatchBase] = useReducer(
    reducer,
    initialState,
    surveyQuestionsSliceToReducer,
  );
  const confirmDelete = useConfirm();
  const { status } = useBuilderContext();

  // Wrap dispatch to intercept removeModule
  const dispatch: React.Dispatch<SurveyModulesDispatch> = (action) => {
    if (action.type === "removeModule" && status === "live") {
      confirmDelete({
        title: "Delete Question?",
        message: confirmDeleteQuestion,
        options: { hide: true, delete: true },
        onDelete: () => dispatchBase(action),
        onHide: () =>
          dispatchBase({ type: "hideModule", payload: action.payload }),
        isHidden: !!state.questions[action.payload]?.hidden,
      });
      return;
    }
    dispatchBase(action);
  };

  return { state, dispatch };
};

function surveyQuestionsSliceToReducer(
  initialState?: BuilderConfigModule[],
): SurveyModulesState {
  if (!initialState || !initialState.length) {
    return {
      route: undefined,
      isOpen: false,
      questions: [],
    };
  }

  const surveyQuestions = initialState.map((question) => {
    switch (question.type) {
      case "Autocomplete": {
        const qType = question as SelectBuilderConfigModule;
        return new AutocompleteModule({
          actionRequired: qType.actionRequired,
          required: qType.required,
          label: qType.label,
          id: question.id,
          options: qType.options,
          randomizeOptions: !!qType.randomizeOptions,
          hidden: qType.hidden,
        });
      }
      case "Dropdown": {
        const qType = question as SelectBuilderConfigModule;
        return new DropdownModule({
          actionRequired: qType.actionRequired,
          required: qType.required,
          label: qType.label,
          id: qType.id,
          options: qType.options,
          randomizeOptions: !!qType.randomizeOptions,
          hidden: qType.hidden,
        });
      }
      case "Html": {
        const qType = question as LabeledBuilderConfigModule;
        return new HTMLModule({
          actionRequired: qType.actionRequired,
          label: qType.label,
          id: question.id,
          hidden: qType.hidden,
          className: qType.className,
          stylesheet: qType.stylesheet,
        });
      }
      case "ImageQuestion": {
        const qType = question as ImageQuestionBuilderConfigModule;
        return new ImageQuestionModule({
          actionRequired: qType.actionRequired,
          required: qType.required,
          label: qType.label,
          id: qType.id,
          showLabels: qType.showLabels,
          options: qType.options,
          hidden: qType.hidden,
        });
      }
      case "image": {
        const qType = question as ImageBuilderConfigModule;
        return new ImageModule({
          actionRequired: qType.actionRequired,
          label: qType.label,
          id: question.id,
          src: qType.src,
          style: qType.style,
          hidden: qType.hidden,
        });
      }
      case "MultipleSelectQuestion": {
        const qType = question as MultiSelectBuilderConfigModule;
        return new MultiSelectQuestionModule({
          actionRequired: qType.actionRequired,
          label: qType.label,
          id: qType.id,
          requiredMin: qType.requiredMin,
          requiredMax: qType.requiredMax,
          options: qType.options,
          hidden: qType.hidden,
        });
      }
      case "RankedQuestion": {
        const qType = question as RankedBuilderConfigModule;
        return new RankedQuestionModule({
          actionRequired: qType.actionRequired,
          required: qType.required,
          label: qType.label,
          id: qType.id,
          displayOrder: qType.displayOrder,
          rankLow: qType.rankLow,
          rankHigh: qType.rankHigh,
          rankRange: qType.rankRange,
          hidden: qType.hidden,
        });
      }
      case "SingleSelectQuestion": {
        const qType = question as SingleSelectBuilderConfigModule;
        return new SingleSelectQuestionModule({
          actionRequired: qType.actionRequired,
          required: qType.required,
          label: qType.label,
          id: qType.id,
          hidden: qType.hidden,
          options: qType.options,
        });
      }
      case "TextQuestion": {
        const qType = question as TextQuestionBuilderConfigModule;
        return new TextQuestionModule({
          actionRequired: qType.actionRequired,
          required: qType.required,
          label: qType.label,
          id: qType.id,
          multiline: qType.multiline,
          hidden: qType.hidden,
        });
      }
      case "text": {
        const qType = question as TextBuilderConfigModule;
        return new TextModule({
          actionRequired: qType.actionRequired,
          label: qType.label,
          id: qType.id,
          caption: qType.caption,
          variants: qType.variants,
          style: qType.style,
          hidden: qType.hidden,
        });
      }
      case "SongsQuestion": {
        const qType = question as SongsQuestionBuilderConfigModule;
        return new SongsQuestionModule({
          label: qType.label,
          id: qType.id,
          options: qType.options,
          required: qType.required,
          requiredMax: qType.requiredMax,
          hidden: qType.hidden,
        });
      }
      default:
        throw new Error(
          `invalid survey module found when rehydrating state: ${question.type}`,
        );
    }
  });

  return {
    route: undefined,
    isOpen: false,
    questions: surveyQuestions,
  };
}
